import { events } from '@estee/elc-service-bus';
import { observable } from 'mobx';

import { getManualOffersByOfferCodeQuery, getViewsQuery } from '~api/OffersAPI/OffersAPI';
import { IPGViewsOffers, IViewsOffers } from '~api/OffersAPI/IOffersApiSdk';

import CartOffers from '~domain/entities/CartOffers/CartOffers';
import ICartOffers from '~domain/entities/CartOffers/ICartOffers';
import IOffer from '~domain/entities/Offer/IOffer';
import Offer from '~domain/entities/Offer/Offer';

import {
    IOffersRepositoryProps,
    IOffersRepository
} from '~repositories/OffersRepository/IOffersRepository';
import { eventEmitter } from '~sharedComponents/EventAdapters';
import { OffersEnum } from '~utils/OffersEnum';

const allViews = [
    Offer.cartOffersInCartView,
    Offer.cartOffersQualifiedView,
    Offer.cartOffersNotQualifiedView,
    Offer.appliedOffers
];

export default class OffersRepository implements IOffersRepository {
    @observable public cartOffers: CartOffers;
    @observable private _views: string[] = [];

    constructor({ cartOffers }: IOffersRepositoryProps) {
        this.cartOffers = cartOffers;
    }

    private mapResponseToOffersObject = (res: IPGViewsOffers, views: string[]) => {
        const offerValues = views.reduce((acc, view: keyof typeof OffersEnum) => {
            const offerType = OffersEnum[view];
            const offersPerView = Object.values(res[view]).map((offer) => {
                const {
                    activation,
                    offer_code,
                    messaging: {
                        offers_tab_cta,
                        offers_tab_description,
                        offers_tab_location,
                        offers_tab_cta_url,
                        offers_tab_applied_cta_url,
                        offers_tab_applied_cta,
                        offers_tab_image,
                        offers_tab_header,
                        offers_tab_subheader,
                        confirm_message,
                        error_message
                    },
                    criteria,
                    benefits: { Skus },
                    offer_id,
                    end_date,
                    drupalNodeContent
                } = offer;

                return ({
                    id: offer_id,
                    skus: Skus?.skus || [],
                    offerCode: offer_code,
                    offerType: activation,
                    offerView: offerType,
                    inCart: offerType === Offer.appliedOffers,
                    header: offers_tab_header,
                    subHeader: offers_tab_subheader,
                    imageUrl: offers_tab_image,
                    description: offers_tab_description,
                    applyLabel: offers_tab_cta,
                    appliedLabel: offers_tab_applied_cta,
                    endDate: end_date,
                    isApplying: false,
                    offerLocation: offers_tab_location?.split(',') || ['offers'],
                    criteria,
                    drupalNodeContent,
                    applyUrl: offers_tab_cta_url,
                    appliedUrl: offers_tab_applied_cta_url,
                    messaging: {
                        confirm_message,
                        error_message
                    }
                } as unknown) as IOffer;
            });

            return { ...acc, [offerType]: offersPerView };
        }, {} as IViewsOffers);

        return offerValues;
    };

    public fetchOffersByViews = async (views: string[]) => {
        try {
            const res = await getViewsQuery(views);
            if (!!res) {
                const mappedOffers = this.mapResponseToOffersObject(res, views);
                this.setOffers(mappedOffers);
                this.notifyServices();
            }
        } catch (e) {
            console.warn(e);
        }
    };

    public fetchAllViews = async () => {
        const views = this._views.length ? this._views : allViews;

        this.fetchOffersByViews(views);
    };

    private setOffers(offers: IViewsOffers) {
        const previousData: ICartOffers = {
            qualifiedOffers: this.cartOffers.qualifiedOffers,
            notQualifiedOffers: this.cartOffers.notQualifiedOffers,
            inCartOffers: this.cartOffers.inCartOffers,
            appliedOffers: this.cartOffers.appliedOffers
        };

        this.cartOffers = CartOffers.fromJSON(offers, previousData);
    }

    public notifyServices() {
        const shoppingBagTabCount = this.cartOffers.shoppingBagTabCount();
        const displayCartTabOffers = shoppingBagTabCount > 0;
        const offersInOfferTab = this.cartOffers.offersTabTotalCount();
        const offerCount = !isNaN(offersInOfferTab) ? offersInOfferTab : 0;

        eventEmitter(events.OFFERS_LOADED, { offerCount, displayCartTabOffers });
    }

    public fetchManualOffersByOfferCodes = async (offerCodes: string[]) => {
        const res = await getManualOffersByOfferCodeQuery(offerCodes);

        return res.data || [];
    };

    public setEnabledViews = (views: string[]) => {
        this._views = [...new Set([...this._views, ...views])];
    };
}
